








































import {
  defineComponent,
  ref,
  provide,
  computed,
  watch,
  toRef,
  onBeforeUnmount,
  onBeforeMount,
} from "@vue/composition-api";
import { Route } from "vue-router";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import {
  ContentFeedType,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import emitter from "@/lib/emitter";
import config from "@/config";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";
import usePostData from "./hooks/usePostData";
import usePostFiles from "./hooks/usePostFiles";
import { UpdatePostData } from "../../types";
import ChoosePostTypeController from "../ChoosePostTypeController";
import ConfirmSendForVerification from "../ConfirmSendForVerification";
import CreatePostView from "../CreatePostView";
import GoingLiveConfirm from "../GoingLiveConfirm";

export default defineComponent({
  name: "CreatePostController",
  components: { ConfirmSendForVerification, CreatePostView, ModalView, ChoosePostTypeController, GoingLiveConfirm },
  inject: [],
  props: {
    postId: { type: String, required: true },
    type: { type: String as () => ContentFeedType, required: true },
    show: { type: Boolean, required: true },
    userId: { type: String, required: true },
  },
  setup(props, { root, emit }) {
    const { postData, isDraft, getPostData, createPost, updatePost, deletePost } = usePostData();
    const loading = ref(false);
    const saving = ref<Promise<any> | null>(null);
    const updateCb = ref<(() => Promise<void>)[]>([]);
    const showLiveConfirm = ref(false);
    const postType = ref<ContentFeedTypeEnum>();
    const updatePostData = ref<UpdatePostData | null>(null);

    const {
      loadingUploadUrls,
      postFileDataList,
      originalPostFilesIds,
      getPostFiles,
      updatePostFiles,
      onAddFiles,
      onAddFilesFromVault,
      onFileRemove,
      removeAllFiles,
      onFileChange,
      onDraggableUpdate,
      onPublicAccessChangeAll,
      stopAndRemoveUploadingFiles,
    } = usePostFiles(
      computed(() => postData.value?.id || props.postId || ""),
      computed(() => postData.value?.type || props.type || ""),
      computed(() => !isDraft.value)
    );

    const showChooseType = computed(() => !loading.value && !postData.value && !showLiveConfirm.value);

    const isPostEmpty = computed(() => {
      if (!postData.value) return true;
      return !(postData.value?.description || postData.value?.title || postFileDataList.value.length > 0);
    });

    const watchPostId = async (postId: string) => {
      if (!postData.value && postId) {
        try {
          loading.value = true;
          await Promise.all([getPostData({ userId: props.userId, postId }), getPostFiles(postId)]);
        } finally {
          loading.value = false;
        }
      }
    };

    watch(toRef(props, "postId"), (postId: string) => watchPostId(postId), { immediate: true });

    const onCreatePost = async (type: ContentFeedTypeEnum, cb?: () => void) => {
      try {
        loading.value = true;
        await createPost(type, props.userId);
      } finally {
        loading.value = false;
        cb && cb();
      }
    };

    const onSetPostType = (type: ContentFeedTypeEnum) => {
      postType.value = type;
    };

    const onSetPostData = (data: ContentSet) => {
      postData.value = data;
      getPostFiles(data.id);
    };

    const onSetUpdateCb = (cb: () => Promise<void>) => {
      updateCb.value.push(cb);
    };

    const reset = () => {
      emit("close");
      if (postData.value && props.type !== ContentFeedTypeEnum.STORY) {
        emitter.emit(EVENTS.CONTENT_FEED_UPDATED, { userId: postData.value.userId, feedId: postData.value.id });
      }
      postData.value = null;
      stopAndRemoveUploadingFiles().then();
    };

    const onUpdate = async ({ data, closeOnSave, beforeUpdate, afterUpdate }: UpdatePostData) => {
      let deferred: Function = () => {};
      try {
        saving.value = new Promise<any>(r => (deferred = r));
        beforeUpdate && (await beforeUpdate());
        await updatePostFiles();
        await updatePost(data);
        if (closeOnSave) {
          reset();
        }
      } finally {
        afterUpdate && afterUpdate();
        deferred();
        saving.value = null;
      }
    };

    const onUpdateData = async (updateData: UpdatePostData) => {
      if (!postData.value) return;
      if (
        config.temporaryRePostVerification &&
        !isDraft.value &&
        (updateData.data.description !== postData.value.description ||
          postFileDataList.value.some(file => !originalPostFilesIds.value.includes(file.id)))
      ) {
        updatePostData.value = updateData;
      } else {
        await onUpdate(updateData);
      }
    };

    const onConfirmSendUpdate = async () => {
      if (!updatePostData.value) return;
      await onUpdate(updatePostData.value);
      updatePostData.value = null;
    };

    const onCancelSendUpdate = async () => {
      updatePostData.value?.afterUpdate && updatePostData.value.afterUpdate();
      updatePostData.value = null;
    };

    const saveDraft = async () => {
      const update = () => Promise.all(updateCb.value.map(i => i()));
      return onUpdate({ data: { published: false }, closeOnSave: false, beforeUpdate: update });
    };

    const deleteDraft = async () => {
      await saving.value;
      await deletePost();
    };

    const onSaveStoryDraft = async () => {
      if (postData.value?.type === ContentFeedTypeEnum.STORY && postFileDataList.value.length !== 0) {
        await saving.value;
        await saveDraft();
        emit("close");
      }
    };

    const onCloseModal = async () => {
      if (postData.value?.type === ContentFeedTypeEnum.STORY && postFileDataList.value.length !== 0) {
        removeAllFiles().then();
        return;
      }
      await saving.value;
      if (postData.value && isDraft.value) {
        isPostEmpty.value ? await deleteDraft() : await saveDraft();
        emit("close");
        return;
      }
      reset();
    };

    onBeforeMount(() => {
      if (props.type in ContentFeedTypeEnum) onSetPostType(props.type as ContentFeedTypeEnum);
    });

    const unWatch = watch(toRef(root, "$route"), (to: Route, from: Route | null) => {
      if (!from || to.name === from.name) return;
      onCloseModal();
    });

    const onPriceChanged = ({
      recurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      if (postData.value?.type === ContentFeedTypeEnum.RICH_TEXT) return;
      const publicAccess = !recurringLevel && !oneTimePackage;
      onPublicAccessChangeAll(publicAccess);
    };
    emitter.on<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
      unWatch();
    });

    provide("contentFeedUserId", props.userId);
    provide("fileList", postFileDataList);
    provide("updatePostData", updatePostData);
    provide("postAddFiles", onAddFiles);
    provide("postAddFilesFromVault", onAddFilesFromVault);
    provide("loadingUploadUrls", loadingUploadUrls);

    return {
      loading,
      showChooseType,
      showLiveConfirm,
      postType,
      postData,
      postFileDataList,
      updatePostData,
      saving,
      onFileRemove,
      onSetUpdateCb,
      onAddFiles,
      onUpdate,
      onSetPostType,
      onSetPostData,
      onCreatePost,
      onCloseModal,
      onAddFilesFromVault,
      onFileChange,
      onDraggableUpdate,
      onSaveStoryDraft,
      onUpdateData,
      onConfirmSendUpdate,
      onCancelSendUpdate,
    };
  },
});
